import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BACKEND_URL } from '../config/api.config';
import { useAuth } from '../context/AuthContext';
import { useOrganizacion } from '../context/OrganizacionContext';

const LoginOTP = () => {
  const [step, setStep] = useState(1); // 1: ingreso de cédula, 2: ingreso OTP, 3: éxito
  const [cedula, setCedula] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [emailHint, setEmailHint] = useState('');
  const navigate = useNavigate();
  const { loginLider } = useAuth();
  const { config, logoUrl } = useOrganizacion();

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    
    if (!cedula.trim()) {
      toast.error('Por favor ingrese su número de cédula');
      return;
    }

    setIsLoading(true);
    
    try {
      // Primero verificar si el usuario existe
      const checkResponse = await fetch(`${BACKEND_URL}/api/auth/check-user/${cedula.trim()}`);
      const checkData = await checkResponse.json();

      if (!checkData.exists) {
        toast.error(checkData.message || 'Usuario no encontrado');
        setIsLoading(false);
        return;
      }

      // Usuario existe, solicitar OTP
      const response = await fetch(`${BACKEND_URL}/api/auth/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cedula: cedula.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error solicitando código OTP');
      }

      toast.success(data.message || 'Código OTP enviado a su correo electrónico');
      setEmailHint(data.email_hint || '');
      setStep(2);
      
    } catch (error) {
      toast.error(error.message || 'Error al solicitar código de verificación');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otpCode.trim()) {
      toast.error('Por favor ingrese el código OTP');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cedula: cedula.trim(),
          otp_code: otpCode.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Código de verificación inválido');
      }

      // Guardar información del usuario
      // console.log('[LOGIN DEBUG] Datos recibidos del backend:', data);
      // console.log('[LOGIN DEBUG] user_info:', data.user_info);
      // console.log('[LOGIN DEBUG] user_info.tipo:', data.user_info.tipo);
      // console.log('[LOGIN DEBUG] Tipo de dato:', typeof data.user_info.tipo);
      
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('user_info', JSON.stringify(data.user_info));
      setUserData(data.user_info);
      
      // Verificar que se guardó correctamente
      const savedUserInfo = localStorage.getItem('user_info');
      // console.log('[LOGIN DEBUG] user_info guardado en localStorage:', savedUserInfo);
      // console.log('[LOGIN DEBUG] user_info parseado:', JSON.parse(savedUserInfo));
      
      // Actualizar el contexto de autenticación
      if (data.user_info.tipo === 'lider') {
        // Crear estructura de líder para el contexto
        const liderData = {
          id: data.user_info.id,
          nombre_completo: data.user_info.nombre,
          documento_identidad: data.user_info.cedula,
          email: data.user_info.email
        };
        
        // Obtener empleados a cargo si es líder
        const empleados = data.user_info.empleados_a_cargo || [];
        
        loginLider(liderData, empleados);
        // console.log('[LOGIN DEBUG] Contexto actualizado para líder');
      }
      
      toast.success('¡Inicio de sesión exitoso!');
      setStep(3);
      
      // Redirigir al dashboard unificado para todos los usuarios
      setTimeout(() => {
        console.log('[LOGIN DEBUG] Redirigiendo a dashboard unificado');
        navigate('/dashboard-unificado');
      }, 1500);
      
    } catch (error) {
      toast.error(error.message || 'Código de verificación inválido');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setIsLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cedula: cedula.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error al reenviar código');
      }

      toast.success('Código OTP reenviado a su correo electrónico');
      
    } catch (error) {
      toast.error(error.message || 'Error al reenviar código');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-2xl">
        <div>
          {/* Logo de la organización */}
          {logoUrl && config.mostrar_logo && (
            <div className="flex justify-center mb-6">
              <img
                src={logoUrl}
                alt={config.nombre_organizacion || 'Logo'}
                className="h-20 w-auto object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
          
          <h2 
            className="mt-6 text-center text-3xl font-extrabold"
            style={{ color: config.color_primario }}
          >
            {step === 1 && 'Inicio de Sesión'}
            {step === 2 && 'Verificar Código OTP'}
            {step === 3 && '¡Bienvenido!'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {step === 1 && 'Ingrese su número de cédula'}
            {step === 2 && 'Ingrese el código enviado a su correo'}
            {step === 3 && 'Redirigiendo...'}
          </p>
        </div>

        {step === 1 && (
          <form className="mt-8 space-y-6" onSubmit={handleRequestOTP}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="cedula" className="sr-only">
                  Número de Cédula
                </label>
                <input
                  id="cedula"
                  name="cedula"
                  type="text"
                  required
                  className="appearance-none rounded-lg relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Número de Cédula"
                  value={cedula}
                  onChange={(e) => setCedula(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isLoading ? 'Enviando...' : 'Enviar Código de Verificación'}
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form className="mt-8 space-y-6" onSubmit={handleVerifyOTP}>
            {emailHint && (
              <div className="rounded-md bg-blue-50 p-4">
                <p className="text-sm text-blue-700">
                  Código enviado a: <strong>{emailHint}</strong>
                </p>
              </div>
            )}

            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="otpCode" className="sr-only">
                  Código OTP
                </label>
                <input
                  id="otpCode"
                  name="otpCode"
                  type="text"
                  required
                  className="appearance-none rounded-lg relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm text-center text-2xl tracking-widest"
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="flex flex-col space-y-3">
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isLoading ? 'Verificando...' : 'Verificar Código'}
              </button>

              <button
                type="button"
                onClick={handleResendOTP}
                disabled={isLoading}
                className="text-sm text-indigo-600 hover:text-indigo-500 disabled:text-gray-400 transition-colors duration-200"
              >
                ¿No recibiste el código? Reenviar
              </button>

              <button
                type="button"
                onClick={() => setStep(1)}
                disabled={isLoading}
                className="text-sm text-gray-600 hover:text-gray-500 disabled:text-gray-400 transition-colors duration-200"
              >
                Cambiar número de cédula
              </button>
            </div>
          </form>
        )}

        {step === 3 && userData && (
          <div className="text-center">
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              ¡Bienvenido, {userData.nombre}!
            </h3>
            <p className="text-sm text-gray-500">
              Redirigiendo a su panel...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginOTP;
